import React, { useCallback, useState } from 'react';
import styles from './styles.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import { IconButton, Tooltip } from '@mui/material';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import ZoomableImage, { DEFAULT_TRANSFORM } from './ZoomableImage';
import EvolutionCompareSlider from './EvolutionCompareSlider';
import EvolutionCompareControls, { COMPARE_MODES } from './EvolutionCompareControls';
import EvolutionPhotoPresentation from './EvolutionPhotoPresentation';
import { formatPhotoDate, isSliderCompatible, photoLabel } from '../../modules/evolutionPhotos';

export { COMPARE_MODES };

const SIDES = Object.freeze([
  { slot: 'before', label: 'Antes' },
  { slot: 'after', label: 'Depois' },
]);

/**
 * Comparador, sempre visível: lado a lado (sempre) ou deslizante (só quando
 * região e ângulo coincidem). O zoom e o deslocamento valem para as duas fotos
 * ao mesmo tempo; nada disso altera o arquivo.
 *
 * Cada lado tem um cabeçalho com etiqueta, data, identificação e os ícones de
 * ver a foto inteira e editar. O lado ativo (`activeSide`, 'before' | 'after')
 * é o que recebe o próximo clique na linha do tempo; escolhe-se clicando no
 * cabeçalho. `after` pode ser nulo (só uma foto): o lado fica vazio.
 *
 * "Tela cheia" abre o modo apresentação (antes e depois com as datas em
 * destaque, para mostrar ao paciente).
 */
const EvolutionPhotoCompare = ({ before, after, activeSide, onActiveSide, enabled, onOpen, onEdit }) => {
  const [mode, setMode] = useState(COMPARE_MODES.SIDE);
  const [transform, setTransform] = useState(DEFAULT_TRANSFORM);
  const [presenting, setPresenting] = useState(false);

  const compatible = isSliderCompatible(before, after);
  const activeMode = compatible ? mode : COMPARE_MODES.SIDE;
  const photoFor = (slot) => (slot === 'before' ? before : after);
  const hasNotes = SIDES.some(({ slot }) => photoFor(slot) && photoFor(slot).note);

  const handleTransform = useCallback((next) => {
    setTransform((current) => (typeof next === 'function' ? next(current) : next));
  }, []);

  function renderHead({ slot, label }) {
    const photo = photoFor(slot);
    const isActive = activeSide === slot;
    return (
      <div key={slot} className={`${styles.sideHead} ${isActive ? styles.sideHeadActive : ''}`}>
        <button
          type="button"
          className={styles.sideSelect}
          aria-pressed={isActive}
          title="A próxima foto escolhida na linha do tempo entra neste lado"
          onClick={() => onActiveSide(slot)}
        >
          <span className={styles.sideBadge}>{label}</span>
          {photo ? (
            <>
              <span className={styles.sideDate}>{formatPhotoDate(photo) || 'Sem data'}</span>
              <span className={styles.sideLabel} title={photoLabel(photo)}>{photoLabel(photo)}</span>
            </>
          ) : (
            <span className={styles.sideLabel}>Sem foto</span>
          )}
        </button>

        {photo && (
          <div className={styles.sideActions}>
            <Tooltip title="Ver foto inteira">
              <IconButton size="small" onClick={() => onOpen(photo)} aria-label="Ver foto inteira">
                <OpenInFullOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            {enabled && (
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => onEdit(photo)} aria-label="Editar foto">
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderStage({ slot }) {
    const photo = photoFor(slot);
    if (!photo) {
      return <div key={slot} className={styles.stageEmpty}>Adicione outra foto para comparar</div>;
    }
    return <ZoomableImage key={slot} attachment={photo} transform={transform} onTransform={handleTransform} />;
  }

  return (
    <div className={styles.compare}>
      <div className={styles.compareHead}>
        <span className={parts.sectionLabel}>Comparação</span>

        <EvolutionCompareControls
          mode={activeMode}
          compatible={compatible}
          hasPair={Boolean(after)}
          onMode={setMode}
          transform={transform}
          onTransform={handleTransform}
        >
          <Tooltip title="Tela cheia para mostrar ao paciente">
            <span>
              <IconButton size="small" onClick={() => setPresenting(true)} disabled={!before} aria-label="Tela cheia">
                <FullscreenOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </EvolutionCompareControls>
      </div>

      <div className={styles.sideHeads}>{SIDES.map(renderHead)}</div>

      {activeMode === COMPARE_MODES.SLIDER ? (
        <EvolutionCompareSlider before={before} after={after} transform={transform} onTransform={handleTransform} />
      ) : (
        <div className={styles.sides}>{SIDES.map(renderStage)}</div>
      )}

      {hasNotes && (
        <div className={styles.sideNotes}>
          {SIDES.map(({ slot }) => {
            const photo = photoFor(slot);
            return <span key={slot} className={styles.sideNote}>{photo && photo.note ? photo.note : ''}</span>;
          })}
        </div>
      )}

      <span className={styles.hint}>
        Clique numa data para trocar a foto do lado destacado · arraste para mover a foto
      </span>

      {presenting && (
        <EvolutionPhotoPresentation
          before={before}
          after={after}
          initialMode={activeMode}
          onClose={() => setPresenting(false)}
        />
      )}
    </div>
  );
};

export default EvolutionPhotoCompare;
